import React from 'react';
import styled from 'styled-components';
import { SxProps } from '@mui/material';
import LoadingButton, {
  type Props as LoadingButtonProps,
} from '../Button/LoadingButton';
import { Flex } from '../Flex';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';

const ButtonWrapper = styled(Flex)`
  position: relative;
  height: 100%;
  :hover {
    background: ${({ theme }) => theme.palette.common.gray};
  }
`;

const InputWrapper = styled(Flex)<{
  $disabled?: boolean;
  $size?: ComponentSize;
}>`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.common.gray1};
  border-radius: 8px;
  height: ${({ $size }) => COMPONENT_SIZE_MAP[$size || 'medium']}px;
  overflow: hidden;
  ${({ $disabled, theme }) =>
    $disabled &&
    `
    background: ${theme.palette.common.gray10};
    cursor: not-allowed;
  `}
`;

export const TextInputLabel = styled.input`
  width: 100%;
  font-size: 14px !important;
  padding: 0 14px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.palette.common.black};
  font-weight: ${({ value, theme }) =>
    value ? theme.typography.fontWeightBold : 400};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.palette.common.slate};
    opacity: 1;
  }

  &:disabled {
    background: ${({ theme }) => theme.palette.common.gray10};
    color: ${({ theme }) => theme.palette.common.gray7};
    cursor: not-allowed;
  }
`;

export const ActionLoadingButton = styled(LoadingButton)<{
  component?: string;
  disabled?: boolean;
}>`
  &.MuiLoadingButton-root {
    height: 100%;
    width: 90px;
    box-shadow: none;
    background: ${({ theme, disabled }) =>
      disabled ? theme.palette.common.gray10 : 'none'};
    color: ${({ theme, disabled }) =>
      disabled ? theme.palette.common.gray7 : theme.palette.common.black};
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.palette.common.black};
    border-radius: 0 8px 8px 0;
    :hover {
      background: none;
      cursor: pointer;
    }
  }
`;

export type TextInputWithButtonProps = {
  value: string;
  onChange: (value: string) => void;
  onButtonClick: () => void;
  placeholder?: string;
  buttonText?: string;
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps;
  size?: ComponentSize;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  buttonProps?: Partial<
    Omit<
      LoadingButtonProps,
      'component' | 'variant' | 'disableElevation' | 'onClick' | 'loading'
    >
  >;
};

const TextInputWithButton = React.forwardRef(
  (
    {
      value = '',
      onChange,
      onButtonClick,
      placeholder,
      buttonText = 'Submit',
      disabled = false,
      loading = false,
      sx,
      size,
      inputProps,
      buttonProps,
    }: TextInputWithButtonProps,
    ref: any,
  ) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    const allButtonProps = {
      ...buttonProps,
      disableElevation: true,
      variant: 'outlined' as const,
      onClick: onButtonClick,
      disabled: disabled || loading,
      loading: loading,
    };

    return (
      <InputWrapper
        ref={ref}
        sx={{ width: '100%', ...sx }}
        $disabled={disabled}
        $size={size}
      >
        <TextInputLabel
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          {...inputProps}
        />
        <ButtonWrapper>
          <ActionLoadingButton {...allButtonProps}>
            {buttonText}
          </ActionLoadingButton>
        </ButtonWrapper>
      </InputWrapper>
    );
  },
);

TextInputWithButton.displayName = 'TextInputWithButton';

export default TextInputWithButton;
